import "@/style.css";

// -------------------------------

import App from "@/App.vue";
import { createApp } from "vue";
import pinia from "@/store.js";
import { useUserStore } from "@/store/user";
import { newRouter } from "@/router.js";
import VueGtag from "vue-gtag";
import { i18n } from "./i18n";
import { initializeCartStore } from "@/store/cart";
import { isB2C, ready } from "@/brandconfig.ts";
import { metaTagPlugin } from "@m-media/vue3-meta-tags";
import { config, isProduction, isLocal } from "./config";
import VueGtm from "vue-gtm";
import { VueQueryPlugin } from "@tanstack/vue-query";
import Clarity from "@microsoft/clarity";
import { xs2eventPlugin } from "@xs2event/js-lib/plugins";
import { initLocales } from "@/components/page/menu/locales.js";
import { setApiPath, enableAppendZ } from "@xs2event/js-lib/api";

// -------------------------------

// Helper function to get cookie preferences
const getCookiePreferences = () => {
  try {
    const stored = localStorage.getItem("cookiePreferences");
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        analytics: Boolean(parsed?.analytics),
      };
    }
  } catch (e) {
    console.error("Failed to parse cookie preferences", e);
  }

  // default: no optional cookies until user consents
  return { analytics: false };
};

//Wait for brandconfig to be ready
(async () => {
  const brandconfig = await ready;
  const cookiePreferences = getCookiePreferences();

  const app = createApp(App);
  app.use(i18n);
  app.use(pinia);
  app.use(VueQueryPlugin, {
    enableDevtoolsV6Plugin: true,
  });

  app.use(xs2eventPlugin, {
    development: isLocal,
    branded: isB2C,
    locales: brandconfig.lang,
    // We only support analytics consent; error monitoring consent was removed.
    // If you want Sentry enabled for all, set this to config.SENTRY_DSN.
    sentryDsn: cookiePreferences.analytics ? config.SENTRY_DSN : undefined,
    appName: config.WEBAPP,
    version: config.VERSION,
    apiPath: config.API_URL,
    cdnPath: config.CDN_URL,
    strapiUrl: config.STRAPI_API_URL,
  });

  useUserStore();
  const router = newRouter();

  initializeCartStore();

  app.use(
    metaTagPlugin,
    {
      defaultName: window.location.href,
      defaultLocale: i18n.global.locale.value,
      locales: brandconfig.locales,
      preconnect: [config.CDN_URL, config.API_URL],
    },
    router
  );
  app.use(router);

  // Only initialize Google Analytics if user has accepted analytics cookies
  if (brandconfig.GA_ID !== "" && cookiePreferences.analytics) {
    app.use(VueGtag, {
      config: { id: brandconfig.GA_ID },
      router,
      enabled: true,
    });
    app.provide("gtag", app.config.globalProperties.$gtag);
  }

  // Only initialize Google Tag Manager if user has accepted analytics cookies
  if (brandconfig.GTM_ID !== "" && cookiePreferences.analytics) {
    app.use(VueGtm, {
      id: brandconfig.GTM_ID,
      defer: false,
      enabled: true,
      debug: false,
      loadScript: true,
      vueRouter: router,
      trackOnNextTick: false,
    });
  }

  initLocales();

  // Only initialize Clarity if user has accepted analytics cookies
  if (isProduction && cookiePreferences.analytics) {
    Clarity.init("pbb7ai4ir4");
  }
  setApiPath(config.API_URL.replace("/v1", ""));
  enableAppendZ();

  app.mount("#app");
})();
